import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, finalize, shareReplay, tap, throwError } from 'rxjs';

import { ApiConfigService } from '../api/api-config.service';
import { TenantService } from '../services/tenant.service';
import {
  AuthUser,
  LoginCredentials,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RegisterRequest,
  RegisterResponse,
} from './auth.models';

interface JwtPayload {
  sub?: string;
  id?: string;
  nome?: string;
  name?: string;
  email?: string;
  role?: string | string[];
  exp?: number;
}

const TOKEN_KEY = 'compleet_access_token';
const REFRESH_TOKEN_KEY = 'compleet_refresh_token';
const ADMIN_ROLES = ['admin', 'support', 'developer', 'owner'];

/**
 * ARC-070: o claim `role` do JWT nem sempre é string — token antigo,
 * usuário multi-role/PBAC (array) ou claim ausente. Ler `.role.toLowerCase()`
 * direto derruba um computed reativo (o erro se propaga no grafo de signals
 * e trava até a rota de login, sem chance de recuperação). Nunca lançar aqui.
 */
export function hasAdminRole(role: string | string[] | undefined): boolean {
  if (Array.isArray(role)) {
    return role.some(r => typeof r === 'string' && ADMIN_ROLES.includes(r.toLowerCase()));
  }
  return typeof role === 'string' && ADMIN_ROLES.includes(role.toLowerCase());
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(ApiConfigService);
  private readonly router = inject(Router);
  private readonly tenantService = inject(TenantService);

  private readonly _user = signal<AuthUser | null>(null);

  /** Refresh em andamento — compartilhado para que 401 paralelos aguardem a mesma renovação. */
  private refreshInFlight$: Observable<LoginResponse> | null = null;

  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);
  readonly canAccessAdmin = computed(() => {
    const user = this._user();
    return user ? hasAdminRole(user.role) : false;
  });

  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(this.api.url('/api/login/forgot-password'), { email });
  }

  resetPassword(email: string, hash: string, newPassword: string): Observable<void> {
    return this.http.post<void>(this.api.url('/api/login/reset-password'), {
      email,
      hash,
      newPassword,
    });
  }

  register(data: RegisterRequest): Observable<RegisterResponse> {
    return this.http
      .post<RegisterResponse>(this.api.url('/api/register'), data)
      .pipe(tap(response => this.storeSession(response)));
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    const body: LoginRequest = {
      user: credentials.email,
      password: credentials.senha,
    };

    return this.http
      .post<LoginResponse>(this.api.url('/api/login'), body)
      .pipe(tap(response => this.storeSession(response)));
  }

  /**
   * Renova o access token via POST /api/login/refresh-token (ARC-042).
   * O backend exige o access token expirado + refresh token vigente.
   * Chamadas concorrentes compartilham a mesma renovação em andamento.
   */
  refreshSession(): Observable<LoginResponse> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    if (!accessToken || !refreshToken) {
      return throwError(() => new Error('Sessão sem refresh token disponível.'));
    }

    const body: RefreshTokenRequest = {
      access_token: accessToken,
      refresh_token: refreshToken,
    };

    this.refreshInFlight$ = this.http
      .post<LoginResponse>(this.api.url('/api/login/refresh-token'), body)
      .pipe(
        tap(response => this.storeSession(response)),
        finalize(() => (this.refreshInFlight$ = null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.refreshInFlight$;
  }

  /**
   * Encerra a sessão limpando TODO o estado (ARC-008): tokens, usuário,
   * tenant e filial — vale para logout explícito e expiração por 401.
   */
  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    this._user.set(null);
    this.tenantService.clear();
    this.router.navigate(['/auth/login']);
  }

  loadUserFromToken(): void {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    try {
      const payload = this.parseJwt(token);
      if (!payload) {
        localStorage.removeItem(TOKEN_KEY);
        return;
      }

      // Access token expirado sem refresh token: sessão realmente acabou.
      // Com refresh token, mantém o access token expirado — o backend exige
      // os dois no refresh e o interceptor renova no primeiro 401 (ARC-042).
      if (this.isTokenExpired(payload) && !this.getRefreshToken()) {
        localStorage.removeItem(TOKEN_KEY);
        return;
      }

      this._user.set({
        id: payload.sub ?? payload.id ?? '',
        nome: payload.nome ?? payload.name ?? '',
        email: payload.email ?? '',
        role: payload.role,
      } satisfies AuthUser);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  private storeSession(response: Pick<LoginResponse, 'access_token' | 'refresh_token'>): void {
    localStorage.setItem(TOKEN_KEY, response.access_token);
    if (response.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);
    }
    this.loadUserFromToken();
  }

  private parseJwt(token: string): JwtPayload | null {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json) as JwtPayload;
  }

  private isTokenExpired(payload: JwtPayload): boolean {
    return payload.exp ? Date.now() >= payload.exp * 1000 : false;
  }
}
